/**
 * TileDetailSheet — level-1 tile detail disclosure (core-flows §4.1).
 *
 * Tapping a tile OUTSIDE edit mode opens this calm bottom sheet (mobile) /
 * floating panel (desktop). It shows expanded per-type content from the tile's
 * already-fetched normalized data, a data-freshness line, and a level-2 external
 * source link (opens provider site in a new tab, rel=noopener).
 *
 * A11y: reuses the AddTileDrawer pattern:
 *   - Focus trap (Tab/Shift+Tab kept inside)
 *   - Escape key dismisses
 *   - Restores focus to the tile that opened the sheet
 *   - aria-modal="true" + role="dialog"
 *
 * Dismiss: backdrop click, Escape, close button, swipe-down (SHOULD #10).
 *
 * Emits: `tile_detail_opened` (depth='detail') on open if analytics enabled.
 * Level-2 source link click emits `tile_detail_opened` (depth='source').
 *
 * Sports sheet only: includes the D-011 league/team picker so the user can
 * configure their team without leaving the detail view. Commits through
 * commitConfigure → configureTile → emits tile_configured.
 *
 * D-010 attribution: "Data from TheSportsDB.com" is shown in the sports detail
 * section and as a calm note in the sports tile chrome area.
 *
 * Immutability: functional component; no prop/state mutation.
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import type { Tile } from 'briefpane/schema';
import type { GlanceViewModel, AnalyticsEmitter } from 'briefpane/contracts';
import type { CommitError } from '../board/use-editable-board.js';
import { buildEnvelope } from '../analytics/identity.js';
import { DetailContent } from './TileDetailSections.js';
import { sourceUrl, sourceName, sourceLinkLabel, freshnessLabel } from './tile-source-links.js';

/* -------------------------------------------------------------------------- */
/* Reduced-motion query (H3)                                                   */
/* -------------------------------------------------------------------------- */

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* -------------------------------------------------------------------------- */
/* Focusable-element query                                                     */
/* -------------------------------------------------------------------------- */

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

/* -------------------------------------------------------------------------- */
/* TileDetailSheet                                                              */
/* -------------------------------------------------------------------------- */

export interface TileDetailSheetProps {
  /** The tile whose detail to show. null = sheet is closed. */
  readonly tile: Tile | null;
  /** The current GlanceViewModel for the tile (from the live cache). */
  readonly vm: GlanceViewModel | null;
  /** Optional enriched data from the adapter (e.g. humidity, wind for weather). */
  readonly data?: Record<string, unknown>;
  /** Called to close the sheet (Escape, backdrop, close button). */
  readonly onClose: () => void;
  /** Called to commit a param change from within the sheet (sports team picker). */
  readonly onConfigure: (
    tileId: string,
    newParams: Record<string, unknown>,
    settingKey?: 'sports_team' | 'watchlist_symbols' | 'news_topic' | 'location' | 'other',
  ) => CommitError;
  /** Analytics emitter. */
  readonly analytics: AnalyticsEmitter;
}

export function TileDetailSheet({
  tile,
  vm,
  data,
  onClose,
  onConfigure,
  analytics,
}: TileDetailSheetProps): React.ReactElement | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const isOpen = tile !== null && vm !== null;

  // Swipe-to-dismiss state (SHOULD #10)
  const swipeStartYRef = useRef<number | null>(null);
  const [swipeTranslateY, setSwipeTranslateY] = useState(0);

  // Reset swipe state when open/close changes.
  useEffect(() => {
    setSwipeTranslateY(0);
    swipeStartYRef.current = null;
  }, [isOpen]);

  // On open: remember trigger element, autofocus close button, emit analytics.
  useEffect(() => {
    if (isOpen && tile) {
      triggerRef.current = document.activeElement;
      const frame = requestAnimationFrame(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = getFocusableElements(dialog);
        if (focusable.length > 0) focusable[0]?.focus();
      });

      // Emit tile_detail_opened (depth='detail') — §1.9
      if (analytics.enabled) {
        try {
          analytics.emit({
            event: 'tile_detail_opened',
            tile_type: tile.type,
            depth: 'detail',
            ...buildEnvelope(),
          });
        } catch { /* analytics never blocks render */ }
      }

      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tile?.id]);

  // On close: restore focus to trigger element.
  useEffect(() => {
    if (!isOpen && triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = getFocusableElements(dialog);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    },
    [onClose],
  );

  if (!isOpen || !tile || !vm) return null;

  const url = sourceUrl(tile);
  const name = sourceName(tile);
  const linkLabel = sourceLinkLabel(tile);
  const freshness = freshnessLabel(vm.fetchedAt);
  const tileLabel = tile.label ?? tile.type;

  function handleSourceLinkClick(): void {
    // Emit level-2 source disclosure event — §1.9
    if (analytics.enabled) {
      try {
        analytics.emit({
          event: 'tile_detail_opened',
          tile_type: tile!.type,
          depth: 'source',
          ...buildEnvelope(),
        });
      } catch { /* analytics never blocks */ }
    }
  }

  return (
    <div
      role="presentation"
      className="sheet-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        display: 'flex',
        alignItems: 'flex-end',
        backgroundColor: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${tileLabel} details`}
        style={{
          background: 'var(--color-bg-tile)',
          borderRadius: 'var(--radius-drawer)',
          width: '100%',
          maxWidth: '480px',
          margin: '0 auto',
          padding: 'var(--space-5)',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-drawer)',
          transform: `translateY(${swipeTranslateY}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => {
          // Only primary touch/mouse
          if (e.button !== 0) return;
          // H2: Don't hijack scrolled content (scroll guard)
          const el = dialogRef.current;
          if (el && el.scrollTop > 0) return;
          // H3: no tracking under reduced motion
          if (prefersReducedMotion()) {
            swipeStartYRef.current = null;
            return;
          }
          swipeStartYRef.current = e.clientY;
          setSwipeTranslateY(0);
        }}
        onPointerMove={(e) => {
          if (swipeStartYRef.current === null) return;
          // H2: If content has scrolled since we started tracking, abort
          const el = dialogRef.current;
          if (el && el.scrollTop > 0) {
            swipeStartYRef.current = null;
            setSwipeTranslateY(0);
            return;
          }
          const deltaY = e.clientY - swipeStartYRef.current;
          if (deltaY > 0) setSwipeTranslateY(deltaY);
        }}
        onPointerUp={(e) => {
          if (swipeStartYRef.current === null) return;
          const deltaY = e.clientY - swipeStartYRef.current;
          swipeStartYRef.current = null;
          // H3: under reduced motion, instant close if threshold met, otherwise nothing
          if (prefersReducedMotion()) {
            const sheetHeight = dialogRef.current?.offsetHeight ?? 400;
            if (deltaY > sheetHeight * 0.3 || deltaY > 80) {
              onClose();
            }
            return;
          }
          const sheetHeight = dialogRef.current?.offsetHeight ?? 400;
          const velocityPx = deltaY; // simplified: no actual velocity calc
          // Dismiss threshold: 30% of height OR > 120px quick swipe
          if (deltaY > sheetHeight * 0.3 || (deltaY > 80 && velocityPx > 80)) {
            setSwipeTranslateY(0);
            onClose();
          } else {
            // Spring back
            setSwipeTranslateY(0);
          }
        }}
        onPointerCancel={() => {
          swipeStartYRef.current = null;
          setSwipeTranslateY(0);
        }}
      >
        {/* Grabber bar (§4.1): coarse-pointer / touch only. */}
        <div data-sheet-grabber aria-hidden="true" />

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}>
          <h2 style={{
            fontSize: 'var(--text-detail)',
            fontWeight: 600,
            margin: 0,
            color: 'var(--color-text-primary)',
          }}>
            {tileLabel}
          </h2>
          <button
            type="button"
            aria-label="Close details"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 'var(--space-1)',
              color: 'var(--color-text-dim)',
              fontSize: 'var(--text-detail)',
              lineHeight: 1,
              borderRadius: 'var(--radius-button)',
            }}
          >
            ✕
          </button>
        </div>

        {/* Per-type expanded detail */}
        <DetailContent
          tile={tile}
          vm={vm}
          {...(data ? { data } : {})}
          onConfigure={onConfigure}
          analytics={analytics}
        />

        {/* Divider + Source / freshness line */}
        {(name || freshness || url) && (
          <div style={{
            borderTop: '1px solid var(--color-border)',
            marginTop: 'var(--space-5)',
            paddingTop: 'var(--space-3)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              fontSize: 'var(--text-timestamp)',
              color: 'var(--color-text-dim)',
            }}>
              {name && <span>Source: {name}</span>}
              {freshness && <span>· {freshness}</span>}
            </div>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSourceLinkClick}
                style={{
                  fontSize: 'var(--text-delta)',
                  color: 'var(--color-accent)',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                {linkLabel}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
